/**
 * useExtensionConfig — lightweight hook that returns the config of an
 * installed-and-enabled Marketplace extension for the current tenant.
 *
 * Caches results in a module-level Map (refreshed on the
 * `marketplace-changed` window event the MarketplacePage fires) so
 * dozens of chart instances don't each hit the API individually.
 *
 * Usage:
 *   const sankeyPlus = useExtensionConfig('mkt_sankey_plus');
 *   if (sankeyPlus?.flow_color_mode === 'by_value') {...}
 *
 * Returns null if the extension is not installed / disabled.
 */

import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

let cachedInstalls = null;
let inflight = null;
const subscribers = new Set();

async function loadInstalls() {
  if (inflight) return inflight;
  inflight = apiFetch(`${API}/api/marketplace/installed`, { method: 'GET' })
    .then(({ ok, data }) => {
      cachedInstalls = ok && Array.isArray(data) ? data : [];
      inflight = null;
      subscribers.forEach(fn => fn(cachedInstalls));
      return cachedInstalls;
    })
    .catch(() => {
      cachedInstalls = [];
      inflight = null;
      return cachedInstalls;
    });
  return inflight;
}

// Invalidate the cache when the marketplace changes (install / uninstall / toggle).
if (typeof window !== 'undefined') {
  window.addEventListener('marketplace-changed', () => {
    cachedInstalls = null;
    loadInstalls();
  });
}

export function useExtensionConfig(extensionId) {
  const [installs, setInstalls] = useState(cachedInstalls || []);

  useEffect(() => {
    let mounted = true;
    const onUpdate = (next) => { if (mounted) setInstalls(next); };
    subscribers.add(onUpdate);
    if (cachedInstalls === null) {
      loadInstalls();
    }
    return () => { mounted = false; subscribers.delete(onUpdate); };
  }, []);

  const match = installs.find(i => i.extension_id === extensionId && i.enabled);
  return match ? (match.config || {}) : null;
}
